import okaoAxios from '../helpers/axios'
import { infoLog, debugLog, errorLog } from '../utils/log-helper'

/**
 * Referral code response from backend
 */
export interface ReferralCodeResponse {
  referralCode: string
  context: string
  url?: string
}

/**
 * Referral counts by status for a specific context
 * e.g. { PENDING: 1, CONFIRMED: 2, COMPLETED: 0 }
 */
export type ReferralCountsByStatus = Record<string, number>

/**
 * Referral status response from GET /referrals/status
 */
export interface ReferralStatusResponse {
  myCodes: ReferralCodeResponse[]
  counts: Record<string, ReferralCountsByStatus> // context -> { status -> count }
}

/**
 * Helper to safely get counts for a specific context with defaults
 * 
 * @param status - The ReferralStatusResponse
 * @param context - The context key (e.g., 'RESTAURANT_SIGNUP', 'EATER_SIGNUP_FROM_TABLE')
 * @returns Object with pending, confirmed, completed, and total counts
 */
export function getReferralCountsForContext(
  status: ReferralStatusResponse,
  context: string,
): {
  pending: number
  confirmed: number
  completed: number
  total: number
} {
  const contextCounts = status.counts?.[context] ?? {}
  const pending = contextCounts.PENDING ?? 0
  const confirmed = contextCounts.CONFIRMED ?? 0
  const completed = contextCounts.COMPLETED ?? 0
  const total = pending + confirmed + completed
  
  return { pending, confirmed, completed, total }
}

/**
 * Select the appropriate referral code based on user type
 * 
 * @param status - The ReferralStatusResponse
 * @param type - User type: 'eater' or 'restaurant'
 * @returns Selected referral code or undefined
 */
export function selectReferralCode(
  status: ReferralStatusResponse,
  type: 'eater' | 'restaurant',
): ReferralCodeResponse | undefined {
  if (type === 'eater') {
    // For eaters: prefer EATER_TO_EATER (friend invites), fallback to RESTAURANT_SIGNUP
    return status.myCodes.find((c) => c.context === 'EATER_TO_EATER')
      ?? status.myCodes.find((c) => c.context === 'RESTAURANT_SIGNUP')
      ?? status.myCodes[0] // Fallback to first code
  } else {
    // For restaurants: use EATER_SIGNUP_FROM_TABLE
    return status.myCodes.find((c) => c.context === 'EATER_SIGNUP_FROM_TABLE')
      ?? status.myCodes[0] // Fallback to first code
  }
}

/**
 * Helper to get total count for a context (sum of all statuses)
 * 
 * @param status - The ReferralStatusResponse
 * @param context - The context key
 * @returns Total count across all statuses
 */
export function getTotalReferralCountForContext(
  status: ReferralStatusResponse,
  context: string,
): number {
  const contextCounts = status.counts?.[context] ?? {}
  return Object.values(contextCounts).reduce((a, b) => a + b, 0)
}

/**
 * Helper to get a specific status count for a context
 * 
 * @param status - The ReferralStatusResponse
 * @param context - The context key
 * @param statusKey - The status key (e.g., 'PENDING', 'CONFIRMED', 'COMPLETED')
 * @returns Count for the specific status, or 0 if not found
 */
export function getReferralCountByStatus(
  status: ReferralStatusResponse,
  context: string,
  statusKey: string,
): number {
  const contextCounts = status.counts?.[context] ?? {}
  return contextCounts[statusKey] ?? 0
}

/**
 * Referral resolve response from GET /referrals/resolve?code=XXXX
 */
export interface ReferralResolveResponse {
  referralCode: string
  context: string
  referrerType: string
  referrerEaterId?: string
  referrerRestaurantId?: string
}

/**
 * Claim referral request
 */
export interface ClaimReferralRequest {
  referralCode: string
}

/**
 * Claim referral response from POST /referrals/claim
 */
export interface ClaimReferralResponse {
  referralId: string
  referralCode: string
  status: string
  context: string
  referrerType: string
  referrerEaterId?: string
  referrerRestaurantId?: string
}

/**
 * Fetch referral status (my codes + counts)
 */
export async function fetchReferralStatus(): Promise<ReferralStatusResponse> {
  const start = performance.now()
  infoLog('[API] fetchReferralStatus start', { url: '/api/referrals/status', method: 'GET' })
  
  try {
    const response = await okaoAxios.get<ReferralStatusResponse>('/api/referrals/status')
    const durationMs = Math.round(performance.now() - start)
    debugLog('[API] fetchReferralStatus success', { status: response.status, durationMs })
    return response.data
  } catch (error: any) {
    const durationMs = Math.round(performance.now() - start)
    errorLog('[API] fetchReferralStatus failed', { status: error.response?.status, durationMs, error })
    throw error
  }
}

/**
 * Resolve a referral code (public, no auth required)
 */
export async function resolveReferral(code: string): Promise<ReferralResolveResponse> {
  const start = performance.now()
  infoLog('[API] resolveReferral start', { url: '/api/referrals/resolve', method: 'GET', params: { code: code.substring(0, 8) + '...' } })
  
  try {
    const response = await okaoAxios.get<ReferralResolveResponse>(`/api/referrals/resolve?code=${encodeURIComponent(code)}`)
    const durationMs = Math.round(performance.now() - start)
    debugLog('[API] resolveReferral success', { status: response.status, durationMs })
    return response.data
  } catch (error: any) {
    const durationMs = Math.round(performance.now() - start)
    errorLog('[API] resolveReferral failed', { status: error.response?.status, durationMs, error })
    throw error
  }
}

/**
 * Claim a referral code (authenticated)
 */
export async function claimReferral(code: string): Promise<ClaimReferralResponse> {
  const start = performance.now()
  infoLog('[API] claimReferral start', { url: '/api/referrals/claim', method: 'POST', params: { code: code.substring(0, 8) + '...' } })
  
  try {
    const response = await okaoAxios.post<ClaimReferralResponse>('/api/referrals/claim', {
      referralCode: code,
    })
    const durationMs = Math.round(performance.now() - start)
    debugLog('[API] claimReferral success', { status: response.status, durationMs, referralId: response.data.referralId })
    return response.data
  } catch (error: any) {
    const durationMs = Math.round(performance.now() - start)
    errorLog('[API] claimReferral failed', { status: error.response?.status, durationMs, error })
    throw error
  }
}
